import React, { createContext, useState, ReactNode } from "react";
import { CATHERINE_O_NEILL, STATUSES } from "../../../../util/constants";

// Define all types directly in the file
interface ColumnOption {
  field: string;
  header: string;
  body?: (rowData: any) => React.ReactNode;
  visible: boolean;
  className?: string;
}

interface Filters {
  size: number;
  first?: number;
  search?: string | null;
  selectedStatuses?: any;
  startDate?: string | null;
  endDate?: string | null;
  dates?: any;
  selectedManager?: any;
  selectedSeniorManager?: any;
  selectedDirector?: any;
  selectedCompany?: any;
  selectedAdminUser?: any;
  selectedAccountantUser?: any;
  selectedInvoiceCreator?: any;
  selectedJobNo?: any;
  selectedManagerInput?: string | null;
  selectedSeniorManagerInput?: string | null;
  selectedDirectorInput?: string | null;
  selectedJobNoInput?: string | null;
  showSelectUserInput?: boolean;
}

interface FilterContextType {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  invoiceColumns: ColumnOption[];
  setInvoiceColumns: React.Dispatch<React.SetStateAction<ColumnOption[]>>;
}

export const FilterContext = createContext<FilterContextType | undefined>(
  undefined,
);

const getInitialStatuses = (): { statusID: number; displayName: string }[] => {
  const roleNamesString = localStorage.getItem("roleName");
  const roleNames = roleNamesString
    ? roleNamesString.split(",").map((name) => name.trim())
    : [];

  const statusMap: Record<string, { statusID: number; displayName: string }[]> =
    {
      "Super Admin": [
        { statusID: 1, displayName: STATUSES.NEW },
        { statusID: 2, displayName: STATUSES.APPROVED_BY_MANAGER },
        { statusID: 3, displayName: STATUSES.REJECTED_BY_MANAGER },
        { statusID: 9, displayName: STATUSES.APPROVED_BY_SENIOR_MANAGER },
        { statusID: 10, displayName: STATUSES.REJECTED_BY_SENIOR_MANAGER },
        { statusID: 4, displayName: STATUSES.APPROVED_BY_DIRECTOR },
        { statusID: 5, displayName: STATUSES.REJECTED_BY_DIRECTOR },
        { statusID: 8, displayName: STATUSES.FINAL_APPROVAL_PENDING },
      ],
      Manager: [{ statusID: 1, displayName: STATUSES.NEW }],
      "Senior Manager": [
        { statusID: 2, displayName: STATUSES.APPROVED_BY_MANAGER },
      ],
      Director: [
        { statusID: 2, displayName: STATUSES.APPROVED_BY_MANAGER },
        { statusID: 9, displayName: STATUSES.APPROVED_BY_SENIOR_MANAGER },
      ],
      Accountant: [{ statusID: 4, displayName: STATUSES.APPROVED_BY_DIRECTOR }],
    };

  const combinedStatuses = roleNames.flatMap(
    (roleName) => statusMap[roleName] || [],
  );

  if (
    localStorage.userEmail === CATHERINE_O_NEILL.userEmail &&
    roleNames.includes("Director")
  ) {
    combinedStatuses.push({
      statusID: 8,
      displayName: STATUSES.FINAL_APPROVAL_PENDING,
    });
  }

  return combinedStatuses.filter(
    (status, index, self) =>
      index === self.findIndex((s) => s.statusID === status.statusID),
  );
};

export const FilterProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<Filters>({
    size: 10,
    first: 0,
    search: null,
    selectedStatuses: getInitialStatuses(),
    startDate: null,
    endDate: null,
    dates: null,
    selectedManager: null,
    selectedSeniorManager: null,
    selectedDirector: null,
    selectedCompany: null,
    selectedAdminUser: null,
    selectedAccountantUser: null,
    selectedInvoiceCreator: null,
    selectedJobNo: null,
    selectedManagerInput: null,
    selectedSeniorManagerInput: null,
    selectedDirectorInput: null,
    selectedJobNoInput: null,
    showSelectUserInput: false,
  });

  const [invoiceColumns, setInvoiceColumns] = useState<ColumnOption[]>([
    { field: "supplierName", header: "Supplier Name", visible: true },
    { field: "billNo", header: "Invoice No.", visible: true },
    { field: "invoiceGeneratedDate", header: "Invoice Date", visible: true },
    { field: "jobNumber", header: "Job No.", visible: true },
    { field: "totalAmount", header: "Amount", visible: true },
    { field: "status", header: "Status", visible: true },
    { field: "poNumber", header: "PO No.", visible: false },
    { field: "companyName", header: "Company", visible: false },
    { field: "managerName", header: "Manager", visible: false },
    { field: "seniorManagerName", header: "Senior Manager", visible: false },
    { field: "directorName", header: "Director", visible: false },
    { field: "commentBySuperAdmin", header: "Admin Notes", visible: false },
    { field: "commentByManager", header: "Manager Notes", visible: false },
    {
      field: "commentBySeniorManager",
      header: "Senior Manager Notes",
      visible: false,
    },
    { field: "commentByDirector", header: "Director Notes", visible: false },
    { field: "createdDate", header: "Uploaded On", visible: false },
  ]);

  return (
    <FilterContext.Provider
      value={{ filters, setFilters, invoiceColumns, setInvoiceColumns }}
    >
      {children}
    </FilterContext.Provider>
  );
};
